import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

export const ContactSection = () => {
  const [leftRef, leftVisible] = useScrollAnimation({ threshold: 0.2 });
  const [formRef, formVisible] = useScrollAnimation({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Get Cloud Functions URL from environment
      // Format: https://REGION-PROJECT_ID.cloudfunctions.net/submitContact
      // For local dev: http://localhost:8080 (using functions-framework)
      const functionsUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Request received! We\'ll review your project and get back to you within 24 hours.');
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      toast.error('Something went wrong. Please try again or email us directly.');
    }
  };

  return (
    <section id="contact" className="py-32 lg:py-40 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-amber-50/30 to-transparent rounded-full blur-3xl opacity-40" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Premium Left Content */}
          <div 
            ref={leftRef}
            className={`transition-all duration-700 ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <span className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 inline-block">Get Started</span>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Ready to Build Your Product?
            </h2>
            <p className="mt-6 text-xl text-slate-600 leading-relaxed font-medium">
              Get a free consultation and see how we can bring your vision to life.
            </p>
            
            {/* Premium Quick CTAs */}
            <div className="mt-12 space-y-5">
              <div className="group flex items-center gap-5 p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/60 hover:border-slate-300 hover:shadow-premium-lg transition-all duration-300 hover:-translate-x-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center flex-shrink-0 group-hover:from-slate-900 group-hover:to-slate-800 shadow-premium group-hover:shadow-premium-lg transition-all duration-300 group-hover:scale-110">
                  <Send className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-base">Get a Free Consultation</p>
                  <p className="text-sm text-slate-500 font-medium mt-1">No commitment, just answers</p>
                </div>
              </div>
              <div className="group flex items-center gap-5 p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200/60 hover:border-slate-300 hover:shadow-premium-lg transition-all duration-300 hover:-translate-x-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center flex-shrink-0 group-hover:from-slate-900 group-hover:to-slate-800 shadow-premium group-hover:shadow-premium-lg transition-all duration-300 group-hover:scale-110">
                  <ArrowRight className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-base">See Our Portfolio</p>
                  <p className="text-sm text-slate-500 font-medium mt-1">View real products we've built</p>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Contact Form */}
          <Card 
            ref={formRef}
            className={`border-slate-200/80 shadow-premium-xl bg-white/80 backdrop-blur-sm hover:shadow-premium-2xl transition-all duration-500 ${
              formVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-6 scale-95'
            }`}
          >
            <CardContent className="p-10">
              {isSubmitted ? (
                <div className="text-center py-16">
                  <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center mx-auto mb-8 shadow-premium-lg">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-transparent opacity-50 animate-pulse" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Request Received!</h3>
                  <p className="text-slate-600 font-medium">We'll review your project and get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-7">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="h-14 bg-slate-50/80 border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 rounded-xl font-medium transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="h-14 bg-slate-50/80 border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 rounded-xl font-medium transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3">
                      Company / Startup <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className="h-14 bg-slate-50/80 border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 rounded-xl font-medium transition-all duration-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What product or feature do you want to build? What challenges are you facing?"
                      required
                      rows={5}
                      className="bg-slate-50/80 border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 rounded-xl font-medium transition-all duration-300"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-bold text-base shadow-premium-lg hover:shadow-premium-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Get My Free Consultation
                        <Send className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
